import Edit from "@mui/icons-material/Edit";
import QATableButton from "components/Design/Table/Controls/QATableButton";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import QATableShortcut from "../../components/Design/Table/Controls/QATableShortcut";
import QATableButtonRow from "../../components/Design/Table/Layout/QATableButtonRow";
import QATable, { ColumnDefinition } from "../../components/Design/Table/QATable";
import { Routing } from "../../data/Constants/Routing";
import { ControllerAPI, ExecutionErrorRO, ExecutionRO, ScenarioRO, WorkerRO } from "../../api/api";
import { DateTime } from "luxon";
import ColorDot from "../../components/Data/ColorDot";
import ColorDotLegend from "../../components/Data/ColorDotLegend";
import { Delete } from "@mui/icons-material";
import ConfirmationDialog from "../../components/Hooks/Dialog/ConfirmationDialog";
import { hasFailed } from "@miragon/react-utils-api";
import { useAppDispatch } from "../../state/Store";
import useSnackbar from "../../components/Hooks/Snackbar/useSnackbar";
import useDialog from "../../components/Hooks/Dialog/useDialog";
import { updateExecutionsManually } from "../../state/ExecutionsState";

interface Props {
    workers: WorkerRO[];
    scenarios: ScenarioRO[];
    executions: ExecutionRO[];
    executionErrors: ExecutionErrorRO[];
}

const ExecutionTable: React.FC<Props> = props => {
    const navigate = useNavigate();
    const dialog = useDialog();
    const dispatch = useAppDispatch();
    const snackbar = useSnackbar();

    const { scenarios, executions, workers, executionErrors } = props;

    const onDelete = useCallback(async (execution: ExecutionRO) => {
        const confirmation = await dialog.open(ConfirmationDialog, {
            title: "Ausführung löschen",
            question: `Wollen Sie die Ausführung "${execution.name}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`,
            acceptButtonTitle: "Löschen"
        });

        if (!confirmation.accepted) {
            return;
        }

        const response = await ControllerAPI.deleteExecution(execution.id);
        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateExecutionsManually({
                delete: [execution]
            }));
            snackbar.onSuccess("Ausführung gelöscht.");
        }
    }, [dialog, snackbar, dispatch]);

    const columns: ColumnDefinition<ExecutionRO>[] = useMemo(() => [
        {
            id: "status",
            width: "25px",
            padding: {
                left: 8,
                right: 0,
                bottom: 0,
                top: 0
            },
            getValue: item => item.state,
            renderCell: item => {
                return (
                    <QATableShortcut target={Routing.Administration.Execution.Edit.Navigate(item.id)}>
                        {item.state === "CREATED" && (
                            <ColorDot size={16} color="orange" />
                        )}
                        {item.state === "STARTED" && (
                            <ColorDot size={16} color="green" />
                        )}
                        {item.state === "FINISHED" && (
                            <ColorDot size={16} color="lightgrey" />
                        )}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "name",
            title: "Name",
            width: "auto",
            sortable: true,
            getValue: item => item.name,
            renderCell: item => {
                return (
                    <QATableShortcut target={Routing.Administration.Execution.Edit.Navigate(item.id)}>
                        {item.name}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "workers",
            title: "Worker",
            width: "100px",
            sortable: false,
            getValue: () => undefined,
            renderCell: item => {
                const count = workers.filter(w => w.workerAssignment?.executionId === item.id && w.workerState === "WORKING").length;
                return (
                    <QATableShortcut target={Routing.Administration.Worker.Navigate()}>
                        {count} Worker
                    </QATableShortcut>
                );
            }
        },
        {
            id: "created",
            title: "Erstellt",
            width: "180px",
            sortable: true,
            getValue: item => item.created,
            renderCell: item => {
                return (
                    <QATableShortcut target={Routing.Administration.Execution.Edit.Navigate(item.id)}>
                        {DateTime.fromISO(item.created).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "scenario",
            title: "Szenario",
            width: "250px",
            getValue: item => item.scenarioId,
            renderCell: item => {
                return (
                    <QATableShortcut target={Routing.Administration.Execution.Edit.Navigate(item.id)}>
                        {scenarios.find(s => s.id === item.scenarioId)?.name ?? "Unbekannt"}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "errors",
            title: "Fehler",
            width: "100px",
            getValue: item => executionErrors.filter(e => e.executionId === item.id).length,
            renderCell: item => {
                return (
                    <QATableShortcut target={Routing.Administration.Execution.Edit.Navigate(item.id)}>
                        {executionErrors.filter(e => e.executionId === item.id).length} Fehler
                    </QATableShortcut>
                );
            }
        },
        {
            id: "actions",
            width: "70px",
            padding: 0,
            getValue: () => undefined,
            renderCell: item => (
                <QATableButtonRow>
                    <QATableButton
                        icon={Edit}
                        onClicked={() => navigate(Routing.Administration.Execution.Edit.Navigate(item.id))} />
                    <QATableButton
                        icon={Delete}
                        onClicked={() => onDelete(item)} />
                </QATableButtonRow>
            )
        }
    ], [executionErrors, navigate, onDelete, scenarios, workers]);

    return (
        <>
            <QATable
                defaultSortByColumn="created"
                defaultSortByColumnDesc
                items={executions}
                defaultPageSize={25}
                getId={item => item.id}
                fallbackText="Keine Ausführungen gefunden."
                columns={columns} />
            <ColorDotLegend items={[
                { color: "orange", name: `${executions.filter(e => e.state === "CREATED").length} Erstellt` },
                { color: "green", name: `${executions.filter(e => e.state === "STARTED").length} Gestartet` },
                { color: "lightgrey", name: `${executions.filter(e => e.state === "FINISHED").length} Fertig` }
            ]} />
        </>
    );
};

export default ExecutionTable;
